/**
 * „Co u vás ve výrobě umíte?" — the wizard's first step (KAN-1473).
 *
 * Moved verbatim out of ``OnboardingStart``; the catalog and what „Přidat
 * vlastní operaci" may offer come in as props, so the step mounts equally in
 * the tenant wizard and in the landing's anonymous pre-registration — where
 * ``customOptions`` is ``null`` (nothing own is offered) and
 * ``allowStartEmpty`` is off (there is no shop to leave empty yet).
 */

import { IngotIcon } from "@/ingot";
import { useTranslation } from "react-i18next";

import { cx } from "@/lib/utils";

import {
  chainKey,
  nextCustomOperationKey,
  type CustomOperationOptions,
  type WizardCatalog,
} from "./model";
import { PickCard } from "./PickCard";
import { stepHeading as heading } from "./StepHeading";
import type { WizardSelection } from "./useWizardSelection";

export function OperationsStep({
  w,
  catalog,
  customOptions,
  allowStartEmpty = true,
}: {
  w: WizardSelection;
  catalog: WizardCatalog | null;
  customOptions: CustomOperationOptions | null;
  allowStartEmpty?: boolean;
}) {
  const { t } = useTranslation("admin");
  const {
    catalogOperations,
    selectedOps,
    toggleOperation,
    customOps,
    removeCustomOperation,
    customForm,
    setCustomForm,
    addCustomOperation,
    startEmpty,
    setStartEmpty,
    setSelectedOps,
    setCustomOps,
    proposedChains,
    confirmedChains,
    toggleChain,
  } = w;
  const nameOf = (slug: string) =>
    catalogOperations.find((op) => op.slug === slug)?.display_name ?? slug;
  return (
    <>
      {heading(
        t("onboardingStart.q.operations"),
        t("onboardingStart.sub.operations"),
      )}
      {catalog !== null && catalogOperations.length === 0 ? (
        <div className="zero" data-testid="onboarding-no-operations">
          <IngotIcon name="info" size={18} />
          <div className="t-small">
            {t("onboardingStart.operations.empty")}
          </div>
        </div>
      ) : (
        <div
          className="pick-grid"
          role="group"
          aria-label={t("onboardingStart.q.operations")}
        >
          {catalogOperations.map((op) => (
            <PickCard
              key={op.slug}
              on={selectedOps.includes(op.slug)}
              onClick={() => toggleOperation(op)}
              title={op.display_name}
              // KAN-1326: the platform's customer-facing text, not
              // counts of machines and materials.
              sub={op.description ?? undefined}
              testId={`onboarding-operation-${op.slug}`}
            />
          ))}
        </div>
      )}
      {proposedChains.length > 0 ? (
        // KAN-2159 — pairs other shops chain in their Maps. Unticked: the
        // edge is a statement about this workshop, so the tenant makes it.
        <div data-testid="onboarding-chains">
          <div className="sect-label">{t("onboardingStart.chains.section")}</div>
          <p className="t-small" style={{ marginTop: 0 }}>
            {t("onboardingStart.chains.hint")}
          </p>
          <div className="stack" style={{ gap: 10 }}>
            {proposedChains.map((chain) => {
              const key = chainKey(chain);
              return (
                <PickCard
                  key={key}
                  on={confirmedChains.includes(key)}
                  onClick={() => toggleChain(key)}
                  title={t("onboardingStart.chains.pair", {
                    source: nameOf(chain.source),
                    target: nameOf(chain.target),
                  })}
                  sub={t("onboardingStart.chains.evidence", {
                    count: chain.shop_count,
                  })}
                  testId={`onboarding-chain-${chain.source}-${chain.target}`}
                />
              );
            })}
          </div>
        </div>
      ) : null}
      {customOptions?.allowed ? (
        <div data-testid="onboarding-custom-operations">
          <div className="sect-label">{t("onboardingStart.custom.section")}</div>
          <div className="stack" style={{ gap: 10 }}>
            {customOps.map((draft) => (
              <div
                key={draft.key}
                className="pick is-on is-static"
                data-testid={`onboarding-custom-operation-${draft.key}`}
              >
                <span className="pick-mark">
                  <IngotIcon name="check" size={13} />
                </span>
                <span className="pick-body">
                  <span className="pick-title">
                    {draft.name}
                    <em>{t("onboardingStart.custom.tag")}</em>
                  </span>
                  <span className="pick-sub">
                    {draft.description || t("onboardingStart.custom.noDescription")}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => removeCustomOperation(draft.key)}
                  aria-label={t("onboardingStart.custom.remove")}
                  title={t("onboardingStart.custom.remove")}
                  data-testid={`onboarding-custom-operation-remove-${draft.key}`}
                >
                  <IngotIcon name="trash" size={14} />
                </button>
              </div>
            ))}
            {customForm === null ? (
              <button
                type="button"
                className="addrow"
                onClick={() =>
                  setCustomForm({
                    key: nextCustomOperationKey(),
                    name: "",
                    description: "",
                    operationCategory:
                      customOptions.operation_categories[0]?.slug ?? "",
                    materialCategory:
                      customOptions.material_categories[0]?.slug ?? "",
                  })
                }
                data-testid="onboarding-custom-operation-add"
              >
                <IngotIcon name="plus" size={15} /> {t("onboardingStart.custom.add")}
              </button>
            ) : (
              <div className="inline-form" data-testid="onboarding-custom-operation-form">
                <div className="inline-form-head">
                  {t("onboardingStart.custom.formTitle")}
                </div>
                <div className="ff">
                  <label className="field">
                    <span className="label">{t("onboardingStart.custom.name")}</span>
                    <input
                      className="input"
                      autoFocus
                      placeholder={t("onboardingStart.custom.namePlaceholder")}
                      value={customForm.name}
                      onChange={(e) =>
                        setCustomForm({ ...customForm, name: e.target.value })
                      }
                      data-testid="onboarding-custom-operation-name"
                    />
                  </label>
                  <label className="field">
                    <span className="label">
                      {t("onboardingStart.custom.description")}{" "}
                      <span className="opt">{t("onboardingStart.custom.optional")}</span>
                    </span>
                    <input
                      className="input"
                      placeholder={t("onboardingStart.custom.descriptionPlaceholder")}
                      value={customForm.description}
                      onChange={(e) =>
                        setCustomForm({ ...customForm, description: e.target.value })
                      }
                      data-testid="onboarding-custom-operation-description"
                    />
                  </label>
                  <label className="field">
                    <span className="label">
                      {t("onboardingStart.custom.operationCategory")}
                    </span>
                    <select
                      className="select"
                      value={customForm.operationCategory}
                      onChange={(e) =>
                        setCustomForm({ ...customForm, operationCategory: e.target.value })
                      }
                      data-testid="onboarding-custom-operation-category"
                    >
                      {customOptions.operation_categories.map((category) => (
                        <option key={category.slug} value={category.slug}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span className="label">
                      {t("onboardingStart.custom.materialCategory")}
                    </span>
                    <select
                      className="select"
                      value={customForm.materialCategory}
                      onChange={(e) =>
                        setCustomForm({ ...customForm, materialCategory: e.target.value })
                      }
                      data-testid="onboarding-custom-operation-material-category"
                    >
                      {customOptions.material_categories.map((category) => (
                        <option key={category.slug} value={category.slug}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="inline-form-foot">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={addCustomOperation}
                    disabled={
                      !customForm.name.trim() ||
                      !customForm.operationCategory ||
                      !customForm.materialCategory
                    }
                    data-testid="onboarding-custom-operation-save"
                  >
                    {t("onboardingStart.custom.save")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setCustomForm(null)}
                  >
                    {t("onboardingStart.custom.cancel")}
                  </button>
                  <span className="t-small" style={{ marginLeft: "auto" }}>
                    {t("onboardingStart.custom.hint")}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
      {allowStartEmpty && catalog !== null && catalogOperations.length > 0 ? (
        <>
          <div className="divider-dashed" />
          <button
            type="button"
            className={cx("emptycard", startEmpty && "is-on")}
            aria-pressed={startEmpty}
            onClick={() => {
              setStartEmpty((on) => !on);
              setSelectedOps([]);
              setCustomOps([]);
              setCustomForm(null);
            }}
            data-testid="onboarding-start-empty"
          >
            <span
              className={cx("pick-mark is-radio", startEmpty && "is-on")}
            />
            <span>
              <b>{t("onboardingStart.operations.emptyShopTitle")}</b>
              <span
                className="t-small"
                style={{ display: "block", marginTop: 3 }}
              >
                {t("onboardingStart.operations.emptyShopText")}
              </span>
            </span>
          </button>
        </>
      ) : null}
    </>
  );
}
